import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["cell", "status", "saveButton", "errors", "newRowModal", "newRowField"]
  static values = { saveUrl: String }

  connect() {
    this.updateSaveState()
  }

  markDirty(event) {
    this.clearErrors()
    const cell = event.currentTarget
    if (this.isNumericCell(cell)) this.sanitizeNumericCell(cell)

    const changed = this.cellValue(cell) !== (cell.dataset.originalValue || "")
    cell.dataset.dirty = changed ? "true" : "false"
    this.setDirtyState(cell, changed)
    this.updateSaveState()
  }

  filterNumericInput(event) {
    if (!this.isNumericCell(event.currentTarget) || event.isComposing) return
    if (event.inputType?.startsWith("delete")) return
    if (event.inputType === "insertLineBreak") {
      event.preventDefault()
      return
    }

    const value = event.data
    if (!value) return
    if (/^[\d,.-]+$/.test(value)) return

    event.preventDefault()
  }

  paste(event) {
    const text = event.clipboardData?.getData("text/plain")
    if (!text || (!text.includes("\t") && !text.includes("\n"))) return

    event.preventDefault()

    const startCell = event.currentTarget
    const startRow = parseInt(startCell.dataset.rowIndex, 10)
    const startCol = parseInt(startCell.dataset.colIndex, 10)
    const rows = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n").filter((row, idx, arr) => row.length > 0 || idx < arr.length - 1)

    rows.forEach((rowText, rowOffset) => {
      rowText.split("\t").forEach((value, colOffset) => {
        const target = this.findCell(startRow + rowOffset, startCol + colOffset)
        if (!target) return

        this.setCellValue(target, value)
        this.markDirty({ currentTarget: target })
      })
    })
  }

  async save() {
    const changes = this.dirtyCells().map(cell => ({
      row_id: cell.dataset.rowId || null,
      client_row_id: cell.dataset.clientRowId || null,
      field: cell.dataset.field,
      value: this.cellValue(cell),
      period: cell.dataset.period || null,
      row_values: this.parseRowValues(cell.dataset.rowValues)
    }))

    if (changes.length === 0) return

    this.clearErrors()
    this.setStatus("Saving…")
    this.saveButtonTarget.disabled = true

    const { response, payload } = await this.persistChanges(changes)

    if (response?.ok && payload.ok) {
      this.dirtyCells().forEach(cell => {
        cell.dataset.originalValue = this.cellValue(cell)
        cell.dataset.dirty = "false"
        this.setDirtyState(cell, false)
      })
      this.setStatus(`Saved ${payload.saved_count} change${payload.saved_count === 1 ? "" : "s"}`)
      if (payload.reload) window.location.reload()
    } else {
      const message = this.errorMessage(payload)
      this.showErrors(message)
      if (payload?.partial) {
        this.setStatus(`Saved ${payload.saved_count} change${payload.saved_count === 1 ? "" : "s"}; some changes failed`)
      } else {
        this.setStatus("Could not save")
      }
      if (payload?.reload) {
        window.location.reload()
        return
      }
    }

    this.updateSaveState({ preserveStatus: !response?.ok || !payload.ok })
  }

  async createPeriodMatrixRow(event) {
    event.preventDefault()
    this.clearErrors()

    const fields = this.newRowFieldTargets
    const rowValues = {}
    let period = null
    let value = null
    let valueField = null

    fields.forEach(field => {
      if (this.isNumericCell(field)) this.sanitizeNumericCell(field)

      const role = field.dataset.newRowRole
      if (role === "key") rowValues[field.dataset.field] = this.cellValue(field)
      if (role === "period") period = this.cellValue(field)
      if (role === "value") {
        value = this.cellValue(field)
        valueField = field.dataset.field
      }
    })

    const missingKey = Object.entries(rowValues).find(([, fieldValue]) => fieldValue === "")
    if (missingKey || !period || !valueField || value === "") {
      this.showErrors("Fill every row key, period, and value before creating the row.")
      return
    }

    const change = {
      row_id: null,
      client_row_id: `period-matrix-new-${Date.now()}`,
      field: valueField,
      value,
      period,
      row_values: rowValues,
      allow_new_key: true
    }

    this.setStatus("Creating row…")
    if (this.hasSaveButtonTarget) this.saveButtonTarget.disabled = true

    const { response, payload } = await this.persistChanges([change])
    if (response?.ok && payload.ok) {
      this.setStatus("Row created")
      window.location.reload()
      return
    }

    this.showErrors(this.errorMessage(payload))
    this.setStatus("Could not create row")
    if (payload?.reload) window.location.reload()
    if (this.hasSaveButtonTarget) this.saveButtonTarget.disabled = this.dirtyCells().length === 0
  }

  async persistChanges(changes) {
    let response
    let payload
    try {
      response = await fetch(this.saveUrlValue, {
        method: "PATCH",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ changes })
      })
      payload = await response.json()
    } catch (error) {
      payload = { errors: [{ message: error.message || "Unable to save changes" }] }
    }

    return { response, payload }
  }

  openNewRowModal() {
    if (!this.hasNewRowModalTarget) return

    this.clearErrors()
    this.newRowModalTarget.classList.remove("hidden")
    this.newRowModalTarget.classList.add("flex")
    this.newRowFieldTargets[0]?.focus()
  }

  closeNewRowModal() {
    if (!this.hasNewRowModalTarget) return

    this.newRowModalTarget.classList.add("hidden")
    this.newRowModalTarget.classList.remove("flex")
  }

  sanitizeModalNumericInput(event) {
    const field = event.currentTarget
    if (!this.isNumericCell(field)) return

    this.sanitizeNumericCell(field)
  }

  dirtyCells() {
    return this.cellTargets.filter(cell => cell.dataset.dirty === "true")
  }

  findCell(row, col) {
    return this.cellTargets.find(cell => parseInt(cell.dataset.rowIndex, 10) === row && parseInt(cell.dataset.colIndex, 10) === col)
  }

  cellValue(cell) {
    if (["INPUT", "SELECT", "TEXTAREA"].includes(cell.tagName)) return cell.value.trim()
    return cell.textContent.trim()
  }

  setCellValue(cell, value) {
    if (cell.tagName === "SELECT") {
      const stringValue = value.trim()
      const matchingOption = Array.from(cell.options).find(option => option.value === stringValue || option.text === stringValue)
      if (matchingOption) {
        cell.value = matchingOption.value
      } else {
        const option = new Option(stringValue, stringValue, true, true)
        cell.add(option)
      }
      return
    }

    const nextValue = this.isNumericCell(cell) ? this.sanitizeNumericValue(value) : value

    if (["INPUT", "TEXTAREA"].includes(cell.tagName)) {
      cell.value = nextValue
      return
    }

    cell.textContent = nextValue
  }

  isNumericCell(cell) {
    return ["Float", "Number", "Integer"].includes(cell.dataset.dataType)
  }

  sanitizeNumericCell(cell) {
    const value = this.cellValue(cell)
    const sanitized = this.sanitizeNumericValue(value)
    if (sanitized === value) return

    this.setCellValue(cell, sanitized)
    this.moveCaretToEnd(cell)
  }

  sanitizeNumericValue(value) {
    let sanitized = value.toString().replace(/[^\d,.-]/g, "")
    const negative = sanitized.includes("-")
    sanitized = sanitized.replace(/-/g, "")
    if (negative) sanitized = `-${sanitized}`

    const parts = sanitized.split(".")
    if (parts.length > 2) sanitized = `${parts.shift()}.${parts.join("")}`

    return sanitized
  }

  moveCaretToEnd(cell) {
    if (document.activeElement !== cell) return

    if (typeof cell.setSelectionRange === "function") {
      const end = cell.value.length
      cell.setSelectionRange(end, end)
    }
  }

  setDirtyState(cell, changed) {
    const elements = [cell, this.cellContainer(cell)].filter((element, index, array) => element && array.indexOf(element) === index)
    elements.forEach(element => {
      element.classList.toggle("bg-amber-100", changed)
      element.classList.toggle("ring-1", changed)
      element.classList.toggle("ring-amber-300", changed)
    })
  }

  cellContainer(cell) {
    return cell.closest("td") || cell
  }

  parseRowValues(value) {
    if (!value) return {}
    try { return JSON.parse(value) } catch { return {} }
  }

  updateSaveState({ preserveStatus = false } = {}) {
    const dirtyCount = this.dirtyCells().length
    if (this.hasSaveButtonTarget) this.saveButtonTarget.disabled = dirtyCount === 0
    if (!preserveStatus && dirtyCount > 0) this.setStatus(`${dirtyCount} unsaved change${dirtyCount === 1 ? "" : "s"}`)
    if (!preserveStatus && dirtyCount === 0) this.setStatus("All changes saved")
  }

  setStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  errorMessage(payload) {
    const errors = payload?.errors || []
    if (errors.length === 0) return "Unable to save changes"

    return errors.map(error => error.message || JSON.stringify(error)).join("; ")
  }

  showErrors(message) {
    if (!this.hasErrorsTarget) return

    this.errorsTarget.textContent = message
    this.errorsTarget.classList.remove("hidden")
  }

  clearErrors() {
    if (!this.hasErrorsTarget) return

    this.errorsTarget.textContent = ""
    this.errorsTarget.classList.add("hidden")
  }
}
